import { components, paths } from "@minuthq/minut-api-types/schema"
import {
  components as componentsUnstable,
  operations as operationsUnstable,
} from "@minuthq/minut-api-types/schema_unstable"

import {
  IBasePaginatedEndpoint,
  IPagingFilter,
} from "src/data/pagination/types/paginationTypes"
import { Maybe } from "src/utils/tsUtil"
import { Defined } from "src/utils/types"

export type THomeGroup =
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}"]["get"]["responses"]["200"]["content"]["application/json"]

/** One homegroup on the overview endpoint, with the state of the units inside it. */
export type THomeGroupOverviewEntry =
  componentsUnstable["schemas"]["HomegroupOverviewEntry"]

/** One floor of a homegroup on the overview endpoint. Only present at `depth=unit`. */
export type THomeGroupOverviewFloor =
  componentsUnstable["schemas"]["HomegroupOverviewFloor"]

/** One unit on a floor. Only present at `depth=unit`. */
export type THomeGroupOverviewUnit =
  componentsUnstable["schemas"]["HomegroupOverviewUnit"]

/**
 * What a monitoring feature is doing on a unit.
 *
 * `off` and `ok` are deliberately separate. Both are quiet, but they mean
 * opposite things to an operator: `ok` is a unit being watched with nothing
 * wrong, while `off` is a unit nobody is watching at all. Collapsing them
 * would read a blind spot as a clean bill of health.
 */
export type TMonitoringState = componentsUnstable["schemas"]["MonitoringState"]

export type THomeGroupOverviewFilter =
  operationsUnstable["HomegroupOverview_overview"]["parameters"]["query"]

export interface IHomeGroupMember {
  member_id: string
  user_id: string
  joined_at: string // format: date-time
  role: THomeGroupRole
  name: string
  email: string
}

export interface IFetchHomeGroupMembers extends IBasePaginatedEndpoint {
  members: IHomeGroupMember[]
}

export enum HomeGroupRank {
  OPERATOR,
  MEMBER,
  ADMIN,
}

export interface IFetchHomeGroups extends IBasePaginatedEndpoint {
  homegroups: THomeGroup[]
}

export interface IFetchHomeGroupHomes extends IBasePaginatedEndpoint {
  homes: IHomeGroupHome[]
}

export type TPostHomeGroup = Defined<
  paths["/organizations/{organization_id}/homegroups"]["post"]["requestBody"]
>["content"]["application/json"]

export interface IPostHomeGroupHome {
  home_id: string
}

export interface IHomeGroupHome {
  id: string
  name: string
}

export type TPatchHomeGroup = Defined<
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}"]["patch"]["requestBody"]
>["content"]["application/json"]

export type THomeGroupFilter = Defined<
  paths["/organizations/{organization_id}/homegroups"]["get"]["parameters"]["query"]
>

export interface IHomeGroupHomesFilter extends IPagingFilter {}

export type TMaybeHomeGroup = Maybe<THomeGroup>

export type THomeGroupType = components["schemas"]["HomegroupType"]

export type TPostImportHomeGroupHomesResponse =
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}/csv/homes"]["post"]["responses"]["201"]["content"]["application/json"]

export type TPostImportHomeGroupHomes400Response =
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}/csv/homes"]["post"]["responses"]["400"]["content"]["application/json"]

export type TPostImportHomeGroupHomes413Response =
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}/csv/homes"]["post"]["responses"]["413"]["content"]["application/json"]

export type TPostImportHomeGroupHomes415Response =
  paths["/organizations/{organization_id}/homegroups/{homegroup_id}/csv/homes"]["post"]["responses"]["415"]["content"]["application/json"]

export type CSVRowError = components["schemas"]["CsvRowError"]

export type THomeGroupRole = Exclude<THomeGroup["user_role"], undefined>
